/**
 * 知识抽查系统 - 管理首页
 * 包含成员管理、知识点管理、会议管理等功能
 */
import React, { useState, useEffect } from 'react';
import { Tabs, Card, Button, Table, Modal, Form, Input, Select, Switch, message, Space, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, BookOutlined, CalendarOutlined, ArrowLeftOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';
import './KnowledgeLotteryManage.css';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const KnowledgeLotteryManage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'knowledge');
  const [loading, setLoading] = useState(false);
  
  // 当URL参数变化时，更新activeTab
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);
  
  // 当activeTab变化时，更新URL参数
  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };
  
  // 成员相关状态
  const [members, setMembers] = useState([]);
  const [memberModalVisible, setMemberModalVisible] = useState(false);
  const [memberForm] = Form.useForm();
  const [editingMemberId, setEditingMemberId] = useState(null);
  
  // 分类相关状态
  const [categories, setCategories] = useState([]);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryForm] = Form.useForm();
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  
  // 知识点相关状态
  const [knowledgeContents, setKnowledgeContents] = useState([]);
  const [knowledgeModalVisible, setKnowledgeModalVisible] = useState(false);
  const [knowledgeForm] = Form.useForm();
  const [editingKnowledgeId, setEditingKnowledgeId] = useState(null);
  
  // 题目相关状态
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [questionForm] = Form.useForm();
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  
  // 会议相关状态
  const [sessions, setSessions] = useState([]);
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [sessionForm] = Form.useForm();
  const [editingSessionId, setEditingSessionId] = useState(null);
  
  // 分组相关状态
  const [groups, setGroups] = useState([]);
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [groupForm] = Form.useForm();
  const [editingGroupId, setEditingGroupId] = useState(null);
  
  // 用户管理相关状态
  const [users, setUsers] = useState([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [userForm] = Form.useForm();
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState({}); // userId -> roleId 映射
  const [userRolesDetail, setUserRolesDetail] = useState({}); // userId -> role对象映射
  
  // 加载成员列表
  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.memberApi.getAllMembers();
      if (response.success) {
        setMembers(response.data || []);
      } else {
        message.error(response.message || '获取成员列表失败');
      }
    } catch (error) {
      console.error('获取成员列表错误:', error);
      message.error(error.message || '获取成员列表失败');
    }
    setLoading(false);
  };
  
  // 加载分类列表
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.categoryApi.getAllCategories();
      if (response.success) {
        setCategories(response.data || []);
      } else {
        message.error(response.message || '获取分类列表失败');
      }
    } catch (error) {
      console.error('获取分类列表错误:', error);
      message.error(error.message || '获取分类列表失败');
    }
    setLoading(false);
  };
  
  // 加载知识点列表
  const fetchKnowledgeContents = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.knowledgeContentApi.getAllKnowledgeContents();
      if (response.success) {
        setKnowledgeContents(response.data || []);
      } else {
        message.error(response.message || '获取知识点列表失败');
      }
    } catch (error) {
      console.error('获取知识点列表错误:', error);
      message.error(error.message || '获取知识点列表失败');
    }
    setLoading(false);
  };
  
  // 加载题目列表
  const fetchQuizQuestions = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.quizQuestionApi.getAllQuizQuestions();
      if (response.success) {
        setQuizQuestions(response.data || []);
      } else {
        message.error(response.message || '获取题目列表失败');
      }
    } catch (error) {
      console.error('获取题目列表错误:', error);
      message.error(error.message || '获取题目列表失败');
    }
    setLoading(false);
  };
  
  // 加载会议列表
  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.sessionApi.getAllSessions();
      if (response.success) {
        setSessions(response.data || []);
      } else {
        message.error(response.message || '获取会议列表失败');
      }
    } catch (error) {
      console.error('获取会议列表错误:', error);
      message.error(error.message || '获取会议列表失败');
    }
    setLoading(false);
  };
  
  // 加载分组列表
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.groupApi.getAllGroups();
      if (response.success) {
        setGroups(response.data || []);
      } else {
        message.error(response.message || '获取分组列表失败');
      }
    } catch (error) {
      console.error('获取分组列表错误:', error);
      message.error(error.message || '获取分组列表失败');
    }
    setLoading(false);
  };
  
  // 获取当前用户信息
  const fetchCurrentUser = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        
        // 检查用户是否有用户管理权限（系统管理员角色）
        // 优先检查角色，如果没有角色则回退到isAdmin字段
        try {
          const token = localStorage.getItem('token');
          if (token && (user._id || user.id)) {
            const roleResponse = await knowledgeLotteryApi.userRoleApi.getUserRoleByUserId(user._id || user.id);
            if (roleResponse.success && roleResponse.data && roleResponse.data.roleId) {
              const role = roleResponse.data.roleId;
              const roleName = String(role.name || '').trim();
              // 系统管理员角色有用户管理权限
              const hasUserManagementPermission = roleName === '系统管理员' || roleName.includes('系统管理员');
              setIsAdmin(hasUserManagementPermission);
              console.log('用户管理权限检查:', { roleName, hasUserManagementPermission });
              return;
            }
          }
        } catch (error) {
          console.error('检查用户角色失败:', error);
        }
        
        // 回退到isAdmin字段检查
        setIsAdmin(user.isAdmin === true);
      }
    } catch (error) {
      console.error('获取当前用户信息失败:', error);
    }
  };

  // 退出登录
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    message.success('已退出登录');
    navigate('/login', { replace: true });
  };

  // 加载角色列表
  const fetchRoles = async () => {
    try {
      const response = await knowledgeLotteryApi.roleApi.getAllRoles();
      if (response.success) {
        setRoles(response.data || []);
      } else {
        console.error('获取角色列表失败:', response.message);
        message.error(response.message || '获取角色列表失败');
      }
    } catch (error) {
      console.error('获取角色列表错误:', error);
      const errorMessage = error.response?.data?.message || error.message || '获取角色列表失败';
      message.error(errorMessage);
    }
  };

  // 加载用户角色关联
  const fetchUserRoles = async () => {
    try {
      const response = await knowledgeLotteryApi.userRoleApi.getAllUserRoles();
      if (response.success) {
        const mapping = {};
        const detailMapping = {};
        (response.data || []).forEach(ur => {
          if (ur.userId && ur.roleId) {
            const userId = ur.userId._id || ur.userId.id;
            const roleId = ur.roleId._id || ur.roleId.id;
            mapping[userId] = roleId;
            detailMapping[userId] = ur.roleId; // 保存完整角色对象
          }
        });
        setUserRoles(mapping);
        setUserRolesDetail(detailMapping);
      }
    } catch (error) {
      console.error('获取用户角色关联错误:', error);
    }
  };

  // 加载用户列表
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await knowledgeLotteryApi.userManagementApi.getAllUsers();
      if (response.success) {
        setUsers(response.data || []);
        // 同时加载用户角色关联
        await fetchUserRoles();
      } else {
        message.error(response.message || '获取用户列表失败');
      }
    } catch (error) {
      console.error('获取用户列表错误:', error);
      message.error(error.message || '获取用户列表失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (activeTab === 'members') {
      fetchMembers();
    } else if (activeTab === 'categories') {
      fetchCategories();
    } else if (activeTab === 'knowledge') {
      fetchKnowledgeContents();
    } else if (activeTab === 'questions') {
      fetchQuizQuestions();
    } else if (activeTab === 'sessions') {
      fetchSessions();
    } else if (activeTab === 'groups') {
      fetchGroups();
    } else if (activeTab === 'users' && isAdmin) {
      fetchRoles();
      fetchUsers();
    }
  }, [activeTab, isAdmin]);
  
  // 用户管理相关函数
  const handleUserAdd = async () => {
    // 确保角色列表已加载
    if (roles.length === 0) {
      await fetchRoles();
    }
    userForm.resetFields();
    setEditingUserId(null);
    setUserModalVisible(true);
  };

  const handleUserEdit = async (record) => {
    // 确保角色列表已加载
    if (roles.length === 0) {
      await fetchRoles();
    }
    
    // 获取用户的角色
    let userRoleId = null;
    try {
      const roleResponse = await knowledgeLotteryApi.userRoleApi.getUserRoleByUserId(record._id);
      if (roleResponse.success && roleResponse.data && roleResponse.data.roleId) {
        userRoleId = roleResponse.data.roleId._id || roleResponse.data.roleId.id;
      }
    } catch (error) {
      console.error('获取用户角色错误:', error);
    }
    
    userForm.setFieldsValue({
      username: record.username,
      name: record.name,
      email: record.email || '',
      isActive: record.isActive !== false,
      roleId: userRoleId
    });
    setEditingUserId(record._id);
    setUserModalVisible(true);
  };

  const handleUserDelete = async (id) => {
    try {
      const response = await knowledgeLotteryApi.userManagementApi.deleteUser(id);
      if (response.success) {
        message.success('删除成功');
        fetchUsers();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const handleUserSubmit = async (values) => {
    try {
      if (editingUserId) {
        // 更新用户
        const updateData = {
          name: values.name,
          isActive: values.isActive !== false
        };
        if (values.password) {
          updateData.password = values.password;
        }
        if (values.email !== undefined) {
          updateData.email = values.email || null; // 允许清空邮箱
        }
        const response = await knowledgeLotteryApi.userManagementApi.updateUser(editingUserId, updateData);
        if (response.success) {
          // 分配角色
          if (values.roleId) {
            try {
              const roleResponse = await knowledgeLotteryApi.userRoleApi.assignRoleToUser({
                userId: editingUserId,
                roleId: values.roleId
              });
              if (!roleResponse.success) {
                throw new Error(roleResponse.message || '角色分配失败');
              }
            } catch (error) {
              console.error('分配角色失败:', error);
              const errorMessage = error.response?.data?.message || error.message || '角色分配失败';
              message.warning(`用户更新成功，但角色分配失败：${errorMessage}`);
            }
          }
          message.success('更新成功');
          setUserModalVisible(false);
          fetchUsers();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        // 创建用户
        const response = await knowledgeLotteryApi.userManagementApi.createUser({
          username: values.username,
          password: values.password,
          name: values.name,
          email: values.email || undefined // 如果为空则不传递
        });
        if (response.success) {
          const newUserId = response.data._id || response.data.id;
          // 分配角色
          if (values.roleId && newUserId) {
            try {
              const roleResponse = await knowledgeLotteryApi.userRoleApi.assignRoleToUser({
                userId: newUserId,
                roleId: values.roleId
              });
              if (!roleResponse.success) {
                throw new Error(roleResponse.message || '角色分配失败');
              }
            } catch (error) {
              console.error('分配角色失败:', error);
              const errorMessage = error.response?.data?.message || error.message || '角色分配失败';
              message.warning(`用户创建成功，但角色分配失败：${errorMessage}`);
            }
          }
          message.success('添加成功');
          setUserModalVisible(false);
          fetchUsers();
        } else {
          message.error(response.message || '添加失败');
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };

  // 成员管理相关函数
  const handleMemberAdd = () => {
    memberForm.resetFields();
    setEditingMemberId(null);
    setMemberModalVisible(true);
  };
  
  const handleMemberEdit = (record) => {
    memberForm.setFieldsValue({
      name: record.name,
      department: record.department,
      position: record.position,
      status: record.status || 'active'
    });
    setEditingMemberId(record.id);
    setMemberModalVisible(true);
  };
  
  const handleMemberDelete = async (id) => {
    try {
      const response = await knowledgeLotteryApi.memberApi.deleteMember(id);
      if (response.success) {
        message.success('删除成功');
        fetchMembers();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };
  
  const handleMemberSubmit = async (values) => {
    try {
      if (editingMemberId) {
        const response = await knowledgeLotteryApi.memberApi.updateMember(editingMemberId, values);
        if (response.success) {
          message.success('更新成功');
          setMemberModalVisible(false);
          fetchMembers();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        const response = await knowledgeLotteryApi.memberApi.createMember(values);
        if (response.success) {
          message.success('添加成功');
          setMemberModalVisible(false);
          fetchMembers();
        } else {
          message.error(response.message || '添加失败');
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };
  
  // 分类管理相关函数
  const handleCategoryAdd = () => {
    categoryForm.resetFields();
    setEditingCategoryId(null);
    setCategoryModalVisible(true);
  };
  
  const handleCategoryEdit = (record) => {
    categoryForm.setFieldsValue({
      name: record.name,
      description: record.description
    });
    setEditingCategoryId(record.id);
    setCategoryModalVisible(true);
  };
  
  const handleCategoryDelete = async (id) => {
    try {
      const response = await knowledgeLotteryApi.categoryApi.deleteCategory(id);
      if (response.success) {
        message.success('删除成功');
        fetchCategories();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };
  
  const handleCategorySubmit = async (values) => {
    try {
      if (editingCategoryId) {
        const response = await knowledgeLotteryApi.categoryApi.updateCategory(editingCategoryId, values);
        if (response.success) {
          message.success('更新成功');
          setCategoryModalVisible(false);
          fetchCategories();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        const response = await knowledgeLotteryApi.categoryApi.createCategory(values);
        if (response.success) {
          message.success('添加成功');
          setCategoryModalVisible(false);
          fetchCategories();
        } else {
          message.error(response.message || '添加失败');
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };
  
  // 知识点管理相关函数
  const handleKnowledgeAdd = () => {
    knowledgeForm.resetFields();
    // 设置默认会议年份为当前年份
    const currentYear = new Date().getFullYear();
    knowledgeForm.setFieldsValue({
      sessionYear: currentYear
    });
    setEditingKnowledgeId(null);
    setKnowledgeModalVisible(true);
  };
  
  const handleKnowledgeEdit = (record) => {
    knowledgeForm.setFieldsValue({
      title: record.title,
      content: record.content,
      categoryId: record.categoryId?.id || record.categoryId,
      sessionYear: record.sessionYear,
      sessionNumber: record.sessionNumber,
      learnedDate: record.learnedDate ? new Date(record.learnedDate).toISOString().split('T')[0] : null
    });
    setEditingKnowledgeId(record.id);
    setKnowledgeModalVisible(true);
  };
  
  const handleKnowledgeDelete = async (id) => {
    try {
      const response = await knowledgeLotteryApi.knowledgeContentApi.deleteKnowledgeContent(id);
      if (response.success) {
        message.success('删除成功');
        fetchKnowledgeContents();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };
  
  const handleKnowledgeSubmit = async (values) => {
    try {
      if (editingKnowledgeId) {
        const response = await knowledgeLotteryApi.knowledgeContentApi.updateKnowledgeContent(editingKnowledgeId, values);
        if (response.success) {
          message.success('更新成功');
          setKnowledgeModalVisible(false);
          fetchKnowledgeContents();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        const response = await knowledgeLotteryApi.knowledgeContentApi.createKnowledgeContent(values);
        if (response.success) {
          message.success('添加成功');
          setKnowledgeModalVisible(false);
          fetchKnowledgeContents();
        } else {
          message.error(response.message || '添加失败');
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };
  
  // 分组管理相关函数
  const handleGroupAdd = () => {
    groupForm.resetFields();
    setEditingGroupId(null);
    setGroupModalVisible(true);
  };
  
  const handleGroupEdit = (record) => {
    groupForm.setFieldsValue({
      name: record.name,
      description: record.description,
      memberIds: record.memberIds?.map(m => m.id || m) || []
    });
    setEditingGroupId(record.id);
    setGroupModalVisible(true);
  };
  
  const handleGroupDelete = async (id) => {
    try {
      const response = await knowledgeLotteryApi.groupApi.deleteGroup(id);
      if (response.success) {
        message.success('删除成功');
        fetchGroups();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };
  
  const handleGroupSubmit = async (values) => {
    try {
      if (editingGroupId) {
        const response = await knowledgeLotteryApi.groupApi.updateGroup(editingGroupId, values);
        if (response.success) {
          message.success('更新成功');
          setGroupModalVisible(false);
          fetchGroups();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        const response = await knowledgeLotteryApi.groupApi.createGroup(values);
        if (response.success) {
          message.success('创建成功');
          setGroupModalVisible(false);
          fetchGroups();
        } else {
          message.error(response.message || '创建失败');
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };
  
  // 会议管理相关函数
  const handleSessionAdd = async () => {
    console.log('创建新会议 - 重置表单');
    sessionForm.resetFields();
    setEditingSessionId(null);
    
    // 动态获取所需数据
    try {
      // 并行获取成员组、知识点和题目列表
      const [groupsResponse, knowledgeResponse, questionsResponse] = await Promise.all([
        knowledgeLotteryApi.groupApi.getAllGroups().catch(() => ({ success: false, data: [] })),
        knowledgeLotteryApi.knowledgeContentApi.getAllKnowledgeContents().catch(() => ({ success: false, data: [] })),
        knowledgeLotteryApi.quizQuestionApi.getAllQuizQuestions().catch(() => ({ success: false, data: [] }))
      ]);
      
      // 更新状态
      if (groupsResponse.success) {
        setGroups(groupsResponse.data || []);
      }
      if (knowledgeResponse.success) {
        setKnowledgeContents(knowledgeResponse.data || []);
      }
      if (questionsResponse.success) {
        setQuizQuestions(questionsResponse.data || []);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      // 即使获取失败也打开模态框，使用已有数据
    }
    
    setSessionModalVisible(true);
  };
  
  const handleSessionEdit = async (record) => {
    // 处理知识点ID：支持多个知识点
    const knowledgeContentIds = record.knowledgeContentIds?.map(kc => kc.id || kc) || 
                                (record.knowledgeContentId ? [record.knowledgeContentId.id || record.knowledgeContentId] : []);
    const participantIds = record.participantIds?.map(p => p.id || p) || [];
    const absentMemberIds = record.absentMemberIds?.map(p => p.id || p) || [];
    
    // 动态获取所需数据
    try {
      // 并行获取成员组、知识点和题目列表
      const [groupsResponse, knowledgeResponse, questionsResponse] = await Promise.all([
        knowledgeLotteryApi.groupApi.getAllGroups().catch(() => ({ success: false, data: [] })),
        knowledgeLotteryApi.knowledgeContentApi.getAllKnowledgeContents().catch(() => ({ success: false, data: [] })),
        knowledgeLotteryApi.quizQuestionApi.getAllQuizQuestions().catch(() => ({ success: false, data: [] }))
      ]);
      
      // 更新状态
      if (groupsResponse.success) {
        setGroups(groupsResponse.data || []);
      }
      if (knowledgeResponse.success) {
        setKnowledgeContents(knowledgeResponse.data || []);
      }
      if (questionsResponse.success) {
        setQuizQuestions(questionsResponse.data || []);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      // 即使获取失败也打开模态框，使用已有数据
    }
    
    console.log('编辑会议 - 准备设置表单值:', {
      recordId: record.id,
      knowledgeContentIds: knowledgeContentIds,
      recordKnowledgeContentIds: record.knowledgeContentIds,
      recordKnowledgeContentId: record.knowledgeContentId
    });
    
    sessionForm.setFieldsValue({
      title: record.title,
      description: record.description,
      sessionDate: record.sessionDate ? new Date(record.sessionDate).toISOString().split('T')[0] : null,
      knowledgeContentIds: knowledgeContentIds, // 使用新字段：多个知识点
      participantIds: participantIds,
      absentMemberIds: absentMemberIds,
      quizQuestionIds: record.quizQuestionIds?.map(q => q.id || q) || []
    });
    
    console.log('编辑会议 - 表单值已设置，editingSessionId:', record.id);
    setEditingSessionId(record.id);
    setSessionModalVisible(true);
  };
  
  const handleSessionDelete = async (id) => {
    try {
      const response = await knowledgeLotteryApi.sessionApi.deleteSession(id);
      if (response.success) {
        message.success('删除成功');
        fetchSessions();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };
  
  const handleSessionSubmit = async (values) => {
    try {
      console.log('提交会议表单:', {
        editingSessionId: editingSessionId,
        values: values,
        knowledgeContentIds: values.knowledgeContentIds
      });
      
      if (editingSessionId) {
        console.log('更新会议，ID:', editingSessionId);
        const response = await knowledgeLotteryApi.sessionApi.updateSession(editingSessionId, values);
        console.log('更新会议响应:', response);
        if (response.success) {
          message.success('更新成功');
          setSessionModalVisible(false);
          setEditingSessionId(null); // 清除编辑ID
          sessionForm.resetFields(); // 重置表单
          fetchSessions();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        console.log('创建新会议');
        const response = await knowledgeLotteryApi.sessionApi.createSession(values);
        console.log('创建会议响应:', response);
        if (response.success) {
          message.success('创建成功');
          setSessionModalVisible(false);
          setEditingSessionId(null); // 确保清除编辑ID
          sessionForm.resetFields(); // 重置表单
          fetchSessions();
        } else {
          message.error(response.message || '创建失败');
        }
      }
    } catch (error) {
      console.error('提交会议表单失败:', error);
      message.error(error.message || '操作失败');
    }
  };
  
  // 题目管理相关函数
  const handleQuestionAdd = () => {
    questionForm.resetFields();
    setEditingQuestionId(null);
    setQuestionModalVisible(true);
  };
  
  const handleQuestionEdit = (record) => {
    questionForm.setFieldsValue({
      question: record.question,
      answer: record.answer,
      knowledgeContentId: record.knowledgeContentId?.id || record.knowledgeContentId,
      difficulty: record.difficulty
    });
    setEditingQuestionId(record.id);
    setQuestionModalVisible(true);
  };
  
  const handleQuestionDelete = async (id) => {
    try {
      const response = await knowledgeLotteryApi.quizQuestionApi.deleteQuizQuestion(id);
      if (response.success) {
        message.success('删除成功');
        fetchQuizQuestions();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };
  
  const handleQuestionSubmit = async (values) => {
    try {
      if (editingQuestionId) {
        const response = await knowledgeLotteryApi.quizQuestionApi.updateQuizQuestion(editingQuestionId, values);
        if (response.success) {
          message.success('更新成功');
          setQuestionModalVisible(false);
          fetchQuizQuestions();
        } else {
          message.error(response.message || '更新失败');
        }
      } else {
        const response = await knowledgeLotteryApi.quizQuestionApi.createQuizQuestion(values);
        if (response.success) {
          message.success('添加成功');
          setQuestionModalVisible(false);
          fetchQuizQuestions();
        } else {
          message.error(response.message || '添加失败');
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };
  
  // 成员表格列定义
  const memberColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department'
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusMap = {
          active: { label: '在岗', color: 'green' },
          inactive: { label: '不在岗', color: 'red' }
        };
        const s = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleMemberEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个成员吗？"
            onConfirm={() => handleMemberDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];
  
  // 分类表格列定义
  const categoryColumns = [
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleCategoryEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个分类吗？"
            onConfirm={() => handleCategoryDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];
  
  // 知识点表格列定义
  const knowledgeColumns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: '分类',
      dataIndex: ['categoryId', 'name'],
      key: 'category'
    },
    {
      title: '关联会议',
      key: 'associatedSession',
      render: (_, record) => {
        // 优先显示所有关联的会议
        if (record.associatedSessions && record.associatedSessions.length > 0) {
          const sessions = record.associatedSessions;
          const latestSession = sessions[0]; // 最新的会议
          
          return (
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {latestSession.title || '-'}
                {sessions.length > 1 && (
                  <Tag color="blue" style={{ marginLeft: '8px', fontSize: '11px' }}>
                    +{sessions.length - 1}个
                  </Tag>
                )}
              </div>
              {latestSession.sessionYear && latestSession.sessionNumber && (
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {latestSession.sessionYear}年第{latestSession.sessionNumber}期会议
                </div>
              )}
              {sessions.length > 1 && (
                <div style={{ marginTop: '4px', fontSize: '11px', color: '#666' }}>
                  共 {sessions.length} 个会议
                </div>
              )}
            </div>
          );
        }
        // 兼容旧数据（只有 associatedSession）
        if (record.associatedSession) {
          const session = record.associatedSession;
          return (
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                {session.title || '-'}
              </div>
              {session.sessionYear && session.sessionNumber && (
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {session.sessionYear}年第{session.sessionNumber}期会议
                </div>
              )}
            </div>
          );
        }
        return '-';
      }
    },
    {
      title: '学习日期',
      key: 'learnedDate',
      render: (_, record) => {
        // 优先使用关联会议的日期
        const date = record.associatedSession?.sessionDate || record.learnedDate;
        if (!date) return '-';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}年${month}月${day}日`;
      }
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => {
        // 优先使用后端返回的isLearned字段（基于会议状态）
        if (record.isLearned !== undefined) {
          return record.isLearned ? (
            <Tag color="success">已学习</Tag>
          ) : (
            <Tag color="default">待学习</Tag>
          );
        }
        // 如果没有isLearned字段，使用原有的逻辑（兼容旧数据）
        if (!record.learnedDate) return '-';
        const learnedDate = new Date(record.learnedDate);
        const now = new Date();
        learnedDate.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        if (learnedDate < now) {
          return <Tag color="success">已学习</Tag>;
        }
        return <Tag color="default">待学习</Tag>;
      }
    },
    {
      title: '负责人',
      key: 'createdBy',
      render: (_, record) => {
        if (record.createdBy) {
          if (typeof record.createdBy === 'object') {
            return (
              <div>
                <div style={{ fontWeight: 'bold' }}>
                  {record.createdBy.name || record.createdBy.email || '未知'}
                </div>
                {record.createdBy.email && record.createdBy.name && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {record.createdBy.email}
                  </div>
                )}
              </div>
            );
          }
          return record.createdBy;
        }
        return <span style={{ color: '#999' }}>系统导入</span>;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleKnowledgeEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个知识点吗？"
            onConfirm={() => handleKnowledgeDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];
  
  // 题目表格列定义
  const questionColumns = [
    {
      title: '题目',
      dataIndex: 'question',
      key: 'question',
      ellipsis: true
    },
    {
      title: '难度',
      dataIndex: 'difficulty',
      key: 'difficulty',
      render: (difficulty) => {
        const colors = { easy: 'green', medium: 'orange', hard: 'red' };
        const labels = { easy: '简单', medium: '中等', hard: '困难' };
        return <Tag color={colors[difficulty]}>{labels[difficulty]}</Tag>;
      }
    },
    {
      title: '负责人',
      key: 'createdBy',
      render: (_, record) => {
        if (record.createdBy) {
          if (typeof record.createdBy === 'object') {
            return (
              <div>
                <div style={{ fontWeight: 'bold' }}>
                  {record.createdBy.name || record.createdBy.email || '未知'}
                </div>
                {record.createdBy.email && record.createdBy.name && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {record.createdBy.email}
                  </div>
                )}
              </div>
            );
          }
          return record.createdBy;
        }
        return <span style={{ color: '#999' }}>系统导入</span>;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleQuestionEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这道题目吗？"
            onConfirm={() => handleQuestionDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];
  
  // 分组表格列定义
  const groupColumns = [
    {
      title: '分组名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '成员数量',
      key: 'memberCount',
      render: (_, record) => {
        const memberIds = record.memberIds || [];
        return memberIds.length;
      }
    },
    {
      title: '成员列表',
      key: 'members',
      render: (_, record) => {
        const memberIds = record.memberIds || [];
        if (memberIds.length === 0) {
          return <span style={{ color: '#999' }}>暂无成员</span>;
        }
        const memberNames = memberIds.map(m => {
          const member = typeof m === 'object' ? m : members.find(mm => mm.id === m);
          return member ? member.name : '未知';
        }).join('、');
        return <span title={memberNames}>{memberNames}</span>;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleGroupEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个分组吗？"
            onConfirm={() => handleGroupDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];
  
  // 会议表格列定义
  const sessionColumns = [
    {
      title: '会议标题',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: '会议日期',
      dataIndex: 'sessionDate',
      key: 'sessionDate',
      render: (date) => date ? new Date(date).toLocaleDateString('zh-CN') : '-'
    },
    {
      title: '关联知识点',
      key: 'knowledgeContents',
      render: (_, record) => {
        // 获取所有知识点（支持多个）
        const knowledgeContents = [];
        if (record.knowledgeContentIds && record.knowledgeContentIds.length > 0) {
          knowledgeContents.push(...record.knowledgeContentIds);
        }
        // 兼容旧字段
        if (record.knowledgeContentId && typeof record.knowledgeContentId === 'object') {
          const oldId = record.knowledgeContentId._id || record.knowledgeContentId.id;
          if (!knowledgeContents.some(kc => (kc._id || kc.id) === oldId)) {
            knowledgeContents.push(record.knowledgeContentId);
          }
        }
        
        if (knowledgeContents.length === 0) {
          return '-';
        }
        
        return (
          <div>
            {knowledgeContents.map((kc, index) => (
              <Tag key={kc._id || kc.id || index} color="blue" style={{ marginBottom: '4px' }}>
                {kc.title || kc}
              </Tag>
            ))}
            {knowledgeContents.length > 1 && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                共 {knowledgeContents.length} 个知识点
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        // 根据会议日期自动计算状态
        let computedStatus = status;
        if (record.sessionDate) {
          const sessionDate = new Date(record.sessionDate);
          const now = new Date();
          sessionDate.setHours(0, 0, 0, 0);
          now.setHours(0, 0, 0, 0);
          computedStatus = now <= sessionDate ? 'pending' : 'completed';
        }
        const statusMap = {
          pending: { label: '待进行', color: 'processing' },
          completed: { label: '已完成', color: 'success' }
        };
        const s = statusMap[computedStatus] || { label: computedStatus, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      }
    },
    {
      title: '负责人',
      key: 'createdBy',
      render: (_, record) => {
        if (record.createdBy) {
          if (typeof record.createdBy === 'object') {
            return (
              <div>
                <div style={{ fontWeight: 'bold' }}>
                  {record.createdBy.name || record.createdBy.email || '未知'}
                </div>
                {record.createdBy.email && record.createdBy.name && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {record.createdBy.email}
                  </div>
                )}
              </div>
            );
          }
          return record.createdBy;
        }
        return <span style={{ color: '#999' }}>系统导入</span>;
      }
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleSessionEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个会议吗？"
            onConfirm={() => handleSessionDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 用户管理表格列定义
  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username'
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || <span style={{ color: '#999' }}>未设置</span>
    },
    {
      title: '管理员',
      dataIndex: 'isAdmin',
      key: 'isAdmin',
      render: (isAdmin) => (
        <Tag color={isAdmin ? 'red' : 'default'}>
          {isAdmin ? '是' : '否'}
        </Tag>
      )
    },
    {
      title: '用户权限',
      key: 'permissions',
      render: (_, record) => {
        // 如果是系统管理员，显示所有权限
        if (record.isAdmin) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              <Tag color="red">系统管理员</Tag>
              <Tag color="blue">知识管理</Tag>
              <Tag color="green">个人产品管理</Tag>
              <Tag color="orange">用户管理</Tag>
            </div>
          );
        }
        
        // 获取用户的角色
        const role = userRolesDetail[record._id];
        if (!role) {
          return <Tag color="default">无权限</Tag>;
        }
        
        // 根据角色权限显示
        const permissions = role.permissions || {};
        const permissionTags = [];
        
        // 检查知识管理权限
        const hasKnowledgePerm = Object.keys(permissions).some(resource => {
          if (resource === 'user' || resource === 'privateFund') return false;
          const resourcePerms = permissions[resource];
          return resourcePerms && (resourcePerms.create === true || resourcePerms.update === true);
        });
        if (hasKnowledgePerm) {
          permissionTags.push(<Tag key="knowledge" color="blue">知识管理</Tag>);
        }
        
        // 检查个人产品管理权限
        if (permissions.privateFund && permissions.privateFund.view === true) {
          permissionTags.push(<Tag key="privateFund" color="green">个人产品管理</Tag>);
        }
        
        // 如果没有权限，显示角色名称
        if (permissionTags.length === 0) {
          return <Tag color="default">{role.name || '普通用户'}</Tag>;
        }
        
        return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{permissionTags}</div>;
      }
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '激活' : '停用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleUserEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个用户吗？"
            onConfirm={() => handleUserDelete(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];
  
  return (
    <div style={{ padding: '24px' }}>
      {/* Header with Back Button */}
      <div style={{ 
        marginBottom: '24px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        paddingBottom: '16px',
        borderBottom: '2px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/knowledge-lottery')}
            style={{ border: 'none', boxShadow: 'none' }}
          >
            返回
          </Button>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#262626' }}>
            知识管理
          </h2>
        </div>
        {currentUser && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              {currentUser.name || currentUser.email}
            </span>
            <Popconfirm
              title="确定要退出登录吗？"
              onConfirm={handleLogout}
              okText="确定"
              cancelText="取消"
            >
              <Button 
                icon={<LogoutOutlined />}
                danger
                size="small"
              >
                退出
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
      
      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          className="knowledge-lottery-tabs"
        >
          {/* 前面四个：知识点管理、题目管理、会议管理、分类管理 - 蓝色配色 */}
          <TabPane
            tab={
              <span style={{ color: '#1890ff' }}>
                <BookOutlined />
                知识点管理
              </span>
            }
            key="knowledge"
          >
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleKnowledgeAdd} style={{ background: '#1890ff', borderColor: '#1890ff' }}>
                添加知识点
              </Button>
            </div>
            <Table
              columns={knowledgeColumns}
              dataSource={knowledgeContents}
              rowKey="id"
              loading={loading}
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
            />
          </TabPane>
          
          <TabPane
            tab={
              <span style={{ color: '#1890ff' }}>
                <BookOutlined />
                题目管理
              </span>
            }
            key="questions"
          >
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleQuestionAdd} style={{ background: '#1890ff', borderColor: '#1890ff' }}>
                添加题目
              </Button>
            </div>
            <Table
              columns={questionColumns}
              dataSource={quizQuestions}
              rowKey="id"
              loading={loading}
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
            />
          </TabPane>
          
          <TabPane
            tab={
              <span style={{ color: '#1890ff' }}>
                <CalendarOutlined />
                会议管理
              </span>
            }
            key="sessions"
          >
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleSessionAdd} style={{ background: '#1890ff', borderColor: '#1890ff' }}>
                创建会议
              </Button>
            </div>
            <Table
              columns={sessionColumns}
              dataSource={sessions}
              rowKey="id"
              loading={loading}
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
            />
          </TabPane>
          
          <TabPane
            tab={
              <span style={{ color: '#1890ff' }}>
                <BookOutlined />
                分类管理
              </span>
            }
            key="categories"
          >
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCategoryAdd} style={{ background: '#1890ff', borderColor: '#1890ff' }}>
                添加分类
              </Button>
            </div>
            <Table
              columns={categoryColumns}
              dataSource={categories}
              rowKey="id"
              loading={loading}
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
            />
          </TabPane>
          
          {/* 后面三个：成员管理、分组管理、用户管理 - 绿色配色 */}
          <TabPane
            tab={
              <span style={{ color: '#52c41a' }}>
                <UserOutlined />
                成员管理
              </span>
            }
            key="members"
          >
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleMemberAdd} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                添加成员
              </Button>
            </div>
            <Table
              columns={memberColumns}
              dataSource={members}
              rowKey="id"
              loading={loading}
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
            />
          </TabPane>
          
          <TabPane
            tab={
              <span style={{ color: '#52c41a' }}>
                <UserOutlined />
                分组管理
              </span>
            }
            key="groups"
          >
            <div style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleGroupAdd} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                创建分组
              </Button>
            </div>
            <Table
              columns={groupColumns}
              dataSource={groups}
              rowKey="id"
              loading={loading}
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
            />
          </TabPane>
          
          {/* 用户管理标签页 - 仅管理员可见 */}
          {isAdmin && (
            <TabPane
              tab={
                <span style={{ color: '#52c41a' }}>
                  <UserOutlined />
                  用户管理
                </span>
              }
              key="users"
            >
              <div style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleUserAdd} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                  添加用户
                </Button>
              </div>
              <Table
                columns={userColumns}
                dataSource={users}
                rowKey="_id"
                loading={loading}
                pagination={{
                  showTotal: (total, range) => `共 ${total} 条`,
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true
                }}
              />
            </TabPane>
          )}
        </Tabs>
      </Card>
      
      {/* 成员编辑模态框 */}
      <Modal
        title={editingMemberId ? '编辑成员' : '添加成员'}
        visible={memberModalVisible}
        onCancel={() => setMemberModalVisible(false)}
        onOk={() => memberForm.submit()}
      >
        <Form form={memberForm} onFinish={handleMemberSubmit} layout="vertical">
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item name="department" label="部门">
            <Input placeholder="请输入部门" />
          </Form.Item>
          <Form.Item name="position" label="职位">
            <Input placeholder="请输入职位" />
          </Form.Item>
          <Form.Item 
            name="status" 
            label="状态" 
            initialValue="active"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              <Option value="active">在岗</Option>
              <Option value="inactive">不在岗（离职/转岗）</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 分类编辑模态框 */}
      <Modal
        title={editingCategoryId ? '编辑分类' : '添加分类'}
        visible={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        onOk={() => categoryForm.submit()}
      >
        <Form form={categoryForm} onFinish={handleCategorySubmit} layout="vertical">
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="请输入分类名称" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={4} placeholder="请输入描述" />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 知识点编辑模态框 */}
      <Modal
        title={editingKnowledgeId ? '编辑知识点' : '添加知识点'}
        visible={knowledgeModalVisible}
        onCancel={() => setKnowledgeModalVisible(false)}
        onOk={() => knowledgeForm.submit()}
        width={800}
      >
        <Form form={knowledgeForm} onFinish={handleKnowledgeSubmit} layout="vertical">
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入标题" />
          </Form.Item>
          <Form.Item
            name="content"
            label="内容"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <TextArea rows={8} placeholder="请输入内容" />
          </Form.Item>
          <Form.Item name="categoryId" label="分类">
            <Select placeholder="请选择分类" allowClear>
              {categories.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item 
            name="sessionYear" 
            label="会议年份"
            initialValue={new Date().getFullYear()}
          >
            <Input type="number" placeholder="请输入会议年份（如：2026）" min={2020} />
          </Form.Item>
          <Form.Item name="sessionNumber" label="会议期数">
            <Input type="number" placeholder="请输入会议期数（如：3）" min={1} />
          </Form.Item>
          <Form.Item 
            name="learnedDate" 
            label="学习日期（可选，如有关联会议则从会议日期获取）"
          >
            <Input type="date" />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 题目编辑模态框 */}
      <Modal
        title={editingQuestionId ? '编辑题目' : '添加题目'}
        visible={questionModalVisible}
        onCancel={() => setQuestionModalVisible(false)}
        onOk={() => questionForm.submit()}
        width={800}
      >
        <Form form={questionForm} onFinish={handleQuestionSubmit} layout="vertical">
          <Form.Item
            name="question"
            label="题目"
            rules={[{ required: true, message: '请输入题目' }]}
          >
            <TextArea rows={4} placeholder="请输入题目" />
          </Form.Item>
          <Form.Item
            name="answer"
            label="答案"
            rules={[{ required: true, message: '请输入答案' }]}
          >
            <TextArea rows={6} placeholder="请输入答案" />
          </Form.Item>
          <Form.Item name="knowledgeContentId" label="关联知识点">
            <Select placeholder="请选择知识点" allowClear>
              {knowledgeContents.map(kc => (
                <Option key={kc.id} value={kc.id}>{kc.title}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="difficulty" label="难度" initialValue="medium">
            <Select>
              <Option value="easy">简单</Option>
              <Option value="medium">中等</Option>
              <Option value="hard">困难</Option>
            </Select>
          </Form.Item>
          </Form>
        </Modal>
        
        {/* 分组编辑模态框 */}
        <Modal
          title={editingGroupId ? '编辑分组' : '创建分组'}
          visible={groupModalVisible}
          onCancel={() => setGroupModalVisible(false)}
          onOk={() => groupForm.submit()}
          width={800}
        >
          <Form form={groupForm} onFinish={handleGroupSubmit} layout="vertical">
            <Form.Item
              name="name"
              label="分组名称"
              rules={[{ required: true, message: '请输入分组名称' }]}
            >
              <Input placeholder="请输入分组名称" />
            </Form.Item>
            <Form.Item name="description" label="分组描述">
              <TextArea rows={4} placeholder="请输入分组描述" />
            </Form.Item>
            <Form.Item name="memberIds" label="成员（可多选）">
              <Select 
                mode="multiple" 
                placeholder="请选择成员" 
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {members.map(member => (
                  <Option key={member.id} value={member.id}>
                    {member.name} {member.department ? `(${member.department})` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
        
        {/* 用户管理编辑模态框 - 仅管理员可见 */}
        {isAdmin && (
          <Modal
            title={editingUserId ? '编辑用户' : '添加用户'}
            visible={userModalVisible}
            onCancel={() => setUserModalVisible(false)}
            onOk={() => userForm.submit()}
            width={600}
          >
            <Form form={userForm} onFinish={handleUserSubmit} layout="vertical">
              <Form.Item
                name="username"
                label="用户名（登录账号）"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { pattern: /^[a-zA-Z0-9_-]+$/, message: '用户名只能包含英文字母、数字、下划线和连字符' },
                  { min: 3, message: '用户名长度不能少于3个字符' },
                  { max: 30, message: '用户名长度不能超过30个字符' }
                ]}
              >
                <Input placeholder="请输入用户名（英文字母、数字、下划线、连字符）" disabled={!!editingUserId} />
              </Form.Item>
              <Form.Item
                name="email"
                label="邮箱（可选）"
                rules={[
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input placeholder="请输入邮箱（可选）" />
              </Form.Item>
              {!editingUserId && (
                <Form.Item
                  name="password"
                  label="密码"
                  rules={[{ required: true, message: '请输入密码' }, { min: 6, message: '密码长度不能少于6个字符' }]}
                >
                  <Input.Password placeholder="请输入密码" />
                </Form.Item>
              )}
              {editingUserId && (
                <Form.Item
                  name="password"
                  label="新密码（留空则不修改）"
                  rules={[{ min: 6, message: '密码长度不能少于6个字符' }]}
                >
                  <Input.Password placeholder="留空则不修改密码" />
                </Form.Item>
              )}
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
              <Form.Item
                name="roleId"
                label="知识管理角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select placeholder="请选择角色">
                  {roles.map(role => (
                    <Option key={role._id || role.id} value={role._id || role.id}>
                      {role.name} {role.description ? `(${role.description})` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {editingUserId && (
                <Form.Item
                  name="isActive"
                  label="状态"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch checkedChildren="激活" unCheckedChildren="停用" />
                </Form.Item>
              )}
            </Form>
          </Modal>
        )}
        
        {/* 会议编辑模态框 */}
        <Modal
          title={editingSessionId ? '编辑会议' : '创建会议'}
          visible={sessionModalVisible}
          onCancel={() => {
            console.log('关闭会议模态框，清除编辑ID');
            setSessionModalVisible(false);
            setEditingSessionId(null);
            sessionForm.resetFields(); // 重置表单，确保下次打开时是干净的
          }}
          onOk={() => sessionForm.submit()}
          width={800}
        >
          <SessionForm 
            form={sessionForm} 
            onFinish={handleSessionSubmit}
            members={members}
            groups={groups}
            knowledgeContents={knowledgeContents}
            quizQuestions={quizQuestions}
            editingSessionId={editingSessionId}
          />
        </Modal>
    </div>
  );
};

// 会议表单组件（支持选择分组和标记请假）
const SessionForm = ({ form, onFinish, members, groups, knowledgeContents, quizQuestions, editingSessionId }) => {
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [absentMemberIds, setAbsentMemberIds] = useState([]);
  
  // 监听表单值变化，用于编辑时初始化
  useEffect(() => {
    const participantIds = form.getFieldValue('participantIds') || [];
    const absentIds = form.getFieldValue('absentMemberIds') || [];
    
    if (editingSessionId && participantIds.length > 0) {
      // 编辑模式：根据 participantIds 和 absentMemberIds 构建成员列表
      const allMemberIds = [...participantIds, ...absentIds];
      const allMembers = allMemberIds.map(id => {
        return members.find(m => m.id === id) || 
               { id, name: '未知成员' };
      }).filter(Boolean);
      
      setGroupMembers(allMembers);
      setAbsentMemberIds(absentIds);
    }
  }, [editingSessionId, form, members]);

  // 当选择分组时，加载该分组的所有成员
  const handleGroupChange = async (groupId) => {
    setSelectedGroupId(groupId);
    if (groupId) {
      try {
        const response = await knowledgeLotteryApi.groupApi.getAvailableMembers(groupId);
        if (response.success) {
          setGroupMembers(response.data || []);
          const allMemberIds = response.data.map(m => m.id || m);
          setAbsentMemberIds([]);
          form.setFieldsValue({ 
            participantIds: allMemberIds,
            absentMemberIds: []
          });
        }
      } catch (error) {
        console.error('获取分组成员失败:', error);
        message.error('获取分组成员失败');
      }
    } else {
      setGroupMembers([]);
      setAbsentMemberIds([]);
      form.setFieldsValue({ participantIds: [], absentMemberIds: [] });
    }
  };

  // 切换成员的请假状态
  const handleToggleAbsent = (memberId) => {
    const newAbsentIds = absentMemberIds.includes(memberId)
      ? absentMemberIds.filter(id => id !== memberId)
      : [...absentMemberIds, memberId];
    
    setAbsentMemberIds(newAbsentIds);
    
    // 参与成员 = 所有成员 - 请假成员
    const allMemberIds = groupMembers.map(m => m.id || m);
    const participantIds = allMemberIds.filter(id => !newAbsentIds.includes(id));
    
    form.setFieldsValue({ 
      participantIds: participantIds,
      absentMemberIds: newAbsentIds
    });
  };
  
  // 表单提交前的处理
  const handleFormFinish = (values) => {
    // 确保 participantIds 和 absentMemberIds 正确设置
    const allMemberIds = groupMembers.map(m => m.id || m);
    const finalAbsentIds = absentMemberIds;
    const finalParticipantIds = allMemberIds.filter(id => !finalAbsentIds.includes(id));
    
    onFinish({
      ...values,
      participantIds: finalParticipantIds,
      absentMemberIds: finalAbsentIds
    });
  };

  return (
    <Form form={form} onFinish={handleFormFinish} layout="vertical">
      <Form.Item
        name="title"
        label="会议标题"
        rules={[{ required: true, message: '请输入会议标题' }]}
      >
        <Input placeholder="请输入会议标题" />
      </Form.Item>
      <Form.Item name="description" label="会议描述">
        <TextArea rows={4} placeholder="请输入会议描述" />
      </Form.Item>
      <Form.Item
        name="sessionDate"
        label="会议日期"
        rules={[{ required: true, message: '请选择会议日期' }]}
      >
        <Input type="date" />
      </Form.Item>
      <Form.Item name="knowledgeContentIds" label="关联知识点（可多选）">
        <Select 
          mode="multiple" 
          placeholder="请选择知识点（可多选）" 
          allowClear
          showSearch
          filterOption={(input, option) =>
            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
          }
        >
          {knowledgeContents.map(kc => (
            <Option key={kc.id} value={kc.id}>{kc.title}</Option>
          ))}
        </Select>
      </Form.Item>
      
      {/* 选择分组 */}
      <Form.Item label="选择成员组">
        <Select 
          placeholder="请选择成员组" 
          allowClear
          value={selectedGroupId}
          onChange={handleGroupChange}
        >
          {groups.map(group => (
            <Option key={group.id} value={group.id}>
              {group.name} ({group.memberIds?.length || 0}人)
            </Option>
          ))}
        </Select>
      </Form.Item>
      
      {/* 显示分组成员，可以标记请假 */}
      {groupMembers.length > 0 && (
        <Form.Item label="成员列表（点击标记为请假）">
          <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px', padding: '12px', minHeight: '100px', maxHeight: '300px', overflowY: 'auto' }}>
            {groupMembers.map(member => {
              const isAbsent = absentMemberIds.includes(member.id || member);
              return (
                <Tag
                  key={member.id || member}
                  onClick={() => handleToggleAbsent(member.id || member)}
                  style={{ 
                    marginBottom: '8px', 
                    cursor: 'pointer',
                    padding: '4px 12px',
                    fontSize: '14px'
                  }}
                  color={isAbsent ? 'red' : 'blue'}
                >
                  {member.name} {member.department ? `(${member.department})` : ''}
                  {isAbsent && ' [请假]'}
                </Tag>
              );
            })}
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            提示：蓝色表示参与，红色表示请假。点击标签可切换状态。
          </div>
        </Form.Item>
      )}
      
      {/* 隐藏的字段，用于提交 */}
      <Form.Item name="participantIds" style={{ display: 'none' }}>
        <Input />
      </Form.Item>
      <Form.Item name="absentMemberIds" style={{ display: 'none' }}>
        <Input />
      </Form.Item>
      
      <Form.Item name="quizQuestionIds" label="关联题目">
        <Select 
          mode="multiple" 
          placeholder="请选择题目" 
          allowClear
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {quizQuestions.map(q => (
            <Option key={q.id} value={q.id}>
              {q.question}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </Form>
  );
};

export default KnowledgeLotteryManage;
